import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Autocomplete } from '@/modules/common';
import { OptionType } from '@/modules/common/types';

import { addSizeSchema, AddSizeValues } from '../validations/add-size';

interface AddSizeDialogProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  sizeOptions: OptionType[];
  existingSizeIds: string[];
  onAdd: (size: {
    sizeId: string;
    value: string;
    quantity: number;
    reservedQuantity: number;
  }) => void;
}

export function AddSizeDialog({
  isOpen,
  onOpenChange,
  sizeOptions,
  existingSizeIds,
  onAdd,
}: AddSizeDialogProps) {
  const defaultValues = { sizeId: '', quantity: undefined };

  const form = useForm<AddSizeValues>({
    resolver: zodResolver(addSizeSchema),
    defaultValues,
  });

  const {
    control,
    handleSubmit,
    reset,
    formState: { isDirty },
  } = form;

  const availableSizeOptions = sizeOptions.filter(
    (o) => !existingSizeIds.includes(o.id)
  );

  const handleClose = (open: boolean) => {
    onOpenChange(open);
    reset(defaultValues);
  };

  const handleFormSubmit = (values: AddSizeValues) => {
    const option = sizeOptions.find((o) => o.id === values.sizeId);
    if (!option) return;

    onAdd({
      sizeId: option.id,
      value: option.value,
      quantity: values.quantity,
      reservedQuantity: 0,
    });

    handleClose(false);
  };

  return (
    <Dialog onOpenChange={handleClose} open={isOpen} modal={false}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Add Size</DialogTitle>
          <DialogDescription>Description</DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form
            onSubmit={(event) => {
              event.preventDefault();
              event.stopPropagation();
              handleSubmit(handleFormSubmit)(event);
            }}
          >
            <div className="flex flex-col gap-4">
              <Autocomplete
                label="Size"
                name="sizeId"
                control={control}
                searchable
                placeholder="Select size"
                options={availableSizeOptions}
              />

              <FormField
                control={control}
                name="quantity"
                render={({ field, formState: { errors } }) => (
                  <FormItem>
                    <FormLabel>Quantity</FormLabel>

                    <FormControl>
                      <Input
                        type="number"
                        placeholder="Enter quantity"
                        error={!!errors?.quantity}
                        {...field}
                      />
                    </FormControl>

                    {!errors?.quantity && (
                      <p className="text-xs text-muted-foreground">
                        Set value from 0 to 1000
                      </p>
                    )}
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <DialogFooter className="mt-6 gap-4">
              <DialogClose asChild>
                <Button variant="outline" data-testid="cancel-button">
                  Cancel
                </Button>
              </DialogClose>

              <Button
                type="submit"
                data-testid="save-button"
                disabled={!isDirty}
              >
                Add size
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
